import {
    deleteJobsId,
    getJobs,
    getJobsId,
    patchJobsIdStatus,
    postJobs,
    putJobsId,
    type ApiResponse,
    type CreateJobPostingBodySchema,
    type GetJobsResponse,
    type UpdateJobSchema,
    type UpdateStatusSchema,
} from '@/generated/api-client'
import { configureOpenApiClient } from '@/lib/api/client/configure-openapi-client'
import type { IJob } from '@/app/types/types'
import type { QueryType } from '@/app/types/types'
import { parseAxiosError } from '@/utils/parse-axios-error.util'

configureOpenApiClient()

export type JobsListQueryParams = {
    paginationModel?: string
    filterModel?: string
    sortModel?: string
    includeJobDetails?: 'true' | 'false'
    showAnalytics?: 'true' | 'false'
    externalJobId?: string
}

export type JobsListResult = {
    rows: QueryType[]
    rowCount: number
    total: number
    page: number
    limit: number
    hasNextPage: boolean
    nextPage: number | null
}

type JobsListPayload = {
    rows?: unknown[]
    rowCount?: number
    total?: number
    page?: number
    limit?: number
    hasNextPage?: boolean
    nextPage?: number | null
}

function normalizeListResponse(data: GetJobsResponse): JobsListResult {
    const payload = data as JobsListPayload
    const rowCount = typeof payload.rowCount === 'number' ? payload.rowCount : Number(payload.total ?? 0)
    const total = typeof payload.total === 'number' ? payload.total : rowCount
    const page = typeof payload.page === 'number' ? payload.page : Number(payload.page ?? 0)
    const limit = typeof payload.limit === 'number' ? payload.limit : Number(payload.limit ?? 25)
    const nextPage = payload.nextPage === null || payload.nextPage === undefined ? null : Number(payload.nextPage)

    return {
        rows: (payload.rows ?? []) as QueryType[],
        rowCount,
        total,
        page,
        limit,
        hasNextPage: Boolean(payload.hasNextPage),
        nextPage,
    }
}

function unwrapApiResponse<T>(response: ApiResponse & { data?: T }): T {
    if (response.data !== undefined && response.data !== null) {
        return response.data as T
    }
    const message = typeof response.message === 'string' ? response.message : 'Request failed'
    throw new Error(message)
}

export async function fetchJobsList(query: JobsListQueryParams): Promise<JobsListResult> {
    const { data } = await getJobs({
        query: {
            paginationModel: query.paginationModel,
            filterModel: query.filterModel,
            sortModel: query.sortModel,
            includeJobDetails: query.includeJobDetails,
            showAnalytics: query.showAnalytics,
            externalJobId: query.externalJobId,
        },
        throwOnError: true,
    })

    return normalizeListResponse(data as GetJobsResponse)
}

export async function fetchJobById(id: number, companySlug: string, params: QueryType = {}): Promise<QueryType> {
    const { data } = await getJobsId({
        path: { id: String(id) },
        query: {
            companySlug,
            ...(params as Record<string, string>),
        } as never,
        throwOnError: true,
    })

    return data as QueryType
}

export async function createJobPosting(data: QueryType): Promise<{ job: IJob; message?: string }> {
    try {
        const { data: response } = await postJobs({
            body: data as CreateJobPostingBodySchema,
            throwOnError: true,
        })

        const apiResponse = response as ApiResponse
        const job = unwrapApiResponse<IJob>(apiResponse)
        return { job, message: typeof apiResponse.message === 'string' ? apiResponse.message : undefined }
    } catch (error) {
        const message = parseAxiosError(error, 'Failed to create job')
        if (message === 'You do not have permission to create this job.') {
            throw new Error(`${message} Try making the recruiter email your own.`)
        }
        throw new Error(message)
    }
}

export async function updateJobPosting(id: number, data: QueryType): Promise<QueryType> {
    const { data: response } = await putJobsId({
        path: { id: String(id) },
        body: data as UpdateJobSchema,
        throwOnError: true,
    })

    return response as QueryType
}

export async function updateJobPostingStatus(id: number, data: QueryType): Promise<QueryType> {
    const { data: response } = await patchJobsIdStatus({
        path: { id: String(id) },
        body: data as UpdateStatusSchema,
        throwOnError: true,
    })

    return response as QueryType
}

export async function deleteJobPosting(id: number, params: QueryType = {}): Promise<{ job: QueryType; message?: string }> {
    const { data: response } = await deleteJobsId({
        path: { id: String(id) },
        query: params as never,
        throwOnError: true,
    })

    const apiResponse = response as ApiResponse
    const job = unwrapApiResponse<IJob>(apiResponse)
    return { job, message: typeof apiResponse.message === 'string' ? apiResponse.message : undefined }
}
